import { useMemo, useState } from "react";
import { Search } from "lucide-react";

/**
 * One option in a searchable list.
 *
 * `group` is what the option is filed under — a team, for a category — and is matched by the
 * search box as well as the label, so typing a team name finds everything in it.
 */
export interface SearchableOption {
  id: string;
  label: string;
  group?: string;

  /**
   * A short qualifier drawn after the label — a category's sensitivity, say.
   *
   * ⚠️ Searched as well as shown, like `group`, so a word somebody can read on a row is a
   * word they can type. A facet that is visible and unsearchable reads as a broken search.
   */
  note?: string;

  /**
   * Why this option cannot be chosen, or null when it can.
   *
   * ⚠️ A REASON rather than a boolean, so the row can say what is wrong with it. A disabled
   * checkbox with nothing beside it is the shape D-123 found on the assignee picker: a
   * refusal that asserts nothing, leaving the reader to guess which of several causes it is.
   *
   * ⚠️ It is a COURTESY and never the control. Whatever refuses this option server-side
   * refuses it whether or not this list draws it greyed.
   */
  disabledReason?: string;
}

/**
 * A list of checkboxes with a search box above it.
 *
 * ⚠️ IT SEARCHES A LIST ALREADY IN THE BROWSER, and that is what makes typing here safe. The
 * queue's SUBJECT search is on submit rather than per keystroke because every row it returns
 * files a PHI read against the searcher (D-78, D-33) — this fetches nothing, reads nothing and
 * audits nothing. It narrows a list of names the reader was already given, so there is no
 * event to file and no reason to make somebody press Enter.
 *
 * ⚠️ It filters the OPTIONS, never the rows. Nothing here is applied to the queue until a
 * checkbox is ticked, so an unmatched search leaves the current selection exactly as it was —
 * which is why the selected-but-unmatched options are still rendered below.
 */
export function SearchableOptions({
  options,
  selected,
  onChange,
  label,
  placeholder,
  searchThreshold = 8,
}: {
  options: SearchableOption[] | null;
  selected: string[];
  onChange: (next: string[]) => void;
  label: string;
  placeholder: string;

  /**
   * How many options there have to be before a search box appears.
   *
   * ⚠️ A search box over six things is furniture. It costs a row of vertical space on the
   * busiest panel in the product and answers a question nobody had.
   */
  searchThreshold?: number;
}) {
  const [term, setTerm] = useState("");

  const matches = useMemo(() => {
    if (options === null) return [];

    const needle = term.trim().toLowerCase();
    if (needle === "") return options;

    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(needle) ||
        (o.group?.toLowerCase().includes(needle) ?? false) ||
        (o.note?.toLowerCase().includes(needle) ?? false),
    );
  }, [options, term]);

  if (options === null) {
    return <p className="py-1.5 text-xs text-muted-foreground">Loading…</p>;
  }

  // ⚠️ It refuses a disabled option here as well as disabling the input, so the one place
  // that decides "may this be picked" is the option itself. A `disabled` attribute alone is a
  // rule held in the markup, and the next caller to render this differently loses it.
  const toggle = (id: string) => {
    if (options?.find((o) => o.id === id)?.disabledReason) return;

    onChange(selected.includes(id) ? selected.filter((s) => s !== id) : [...selected, id]);
  };

  // ⚠️ Selected options that the search does not match are still drawn, under a rule rather
  // than at the top: a filter that is applied, invisible and impossible to lift on its own is
  // the failure the whole filter bar exists to prevent. Typing "printer" must not hide the
  // category you already ticked.
  const shown = new Set(matches.map((o) => o.id));
  const hiddenButSelected = options.filter((o) => selected.includes(o.id) && !shown.has(o.id));

  const row = (option: SearchableOption) => (
    <label
      key={option.id}
      title={option.disabledReason}
      className={
        option.disabledReason
          ? "flex items-center gap-2 rounded-md px-1.5 py-1 text-xs opacity-40"
          : "flex cursor-pointer items-center gap-2 rounded-md px-1.5 py-1 text-xs hover:bg-muted"
      }
    >
      <input
        type="checkbox"
        className="h-3.5 w-3.5 shrink-0 accent-primary"
        disabled={Boolean(option.disabledReason)}
        checked={selected.includes(option.id)}
        onChange={() => toggle(option.id)}
      />
      <span className="min-w-0 flex-1 truncate">
        {option.label}
        {option.group && <span className="ml-1 text-muted-foreground">· {option.group}</span>}
      </span>

      {option.note && (
        <span className="shrink-0 text-[10px] text-muted-foreground">{option.note}</span>
      )}
    </label>
  );

  return (
    <div role="group" aria-label={label} className="rounded-lg border border-input bg-card">
      {options.length >= searchThreshold && (
        <div className="flex items-center gap-1.5 border-b border-border px-2 py-1.5">
          <Search className="h-3 w-3 shrink-0 text-muted-foreground" aria-hidden />
          <input
            type="search"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder={placeholder}
            aria-label={`Search ${label.toLowerCase()}`}
            className="min-w-0 flex-1 bg-transparent text-xs outline-none placeholder:text-muted-foreground"
          />
        </div>
      )}

      <div className="max-h-40 overflow-y-auto p-1">
        {matches.map(row)}

        {matches.length === 0 && hiddenButSelected.length === 0 && (
          <p className="px-1.5 py-2 text-xs text-muted-foreground">Nothing matches that.</p>
        )}

        {hiddenButSelected.length > 0 && (
          <>
            {matches.length > 0 && <div className="my-1 border-t border-border" />}
            <p className="px-1.5 pb-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
              Selected
            </p>
            {hiddenButSelected.map(row)}
          </>
        )}
      </div>
    </div>
  );
}
